import { Button, Icon, MinusSVG, PlusSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useMemo } from "react";
import { IStandard } from "@/search/Langston/types/IStandard";
import { STANDARDS_SET_MODE } from "@/search/Langston/types/IStandardsSetMode";
import { useStandardsModalContext } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModalProvider";
import { addRemoveButtonCss } from "./StandardItemButtons.styles";

interface IStandardItemButtonsProps {
  standard: IStandard;
}

const ButtonWithTrackEvent = withTrackEvent(Button);

export const StandardItemButtons = ({
  standard,
}: IStandardItemButtonsProps) => {
  const {
    getters: {
      newStandardsSet: {
        id: standardsSetId,
        title,
        children: standardsInNewSet,
        viewmodel_type,
      },
      standardsSetMode,
      confirmModalData: { editData },
      isPublishingStandardsSet,
      isUpdatingStandardsSet,
    },
    handlers: { setNewStandardsSet, setConfirmModalData },
  } = useStandardsModalContext();

  const isCreateMode = standardsSetMode === STANDARDS_SET_MODE.CREATE;
  const isEditMode = standardsSetMode === STANDARDS_SET_MODE.EDIT;

  const isStandardInNewSet = useMemo(
    () => standardsInNewSet.filter((std) => std.id === standard.id).length > 0,
    [standardsInNewSet],
  );

  const isStandardOnEditSet = useMemo(
    () =>
      editData &&
      editData.children.filter((std) => std.id === standard.id).length > 0,
    [editData],
  );

  const addStandardToSet = () => {
    if (isCreateMode) {
      setNewStandardsSet({
        id: standardsSetId,
        title,
        children: [...standardsInNewSet, standard],
        viewmodel_type,
      });
    } else if (isEditMode && editData) {
      setConfirmModalData({
        setId: editData.id,
        editData: {
          ...editData,
          children: [...editData.children, standard],
        },
      });
    }
  };

  const removeStandardFromSet = () => {
    if (isCreateMode && !isPublishingStandardsSet) {
      setNewStandardsSet({
        id: standardsSetId,
        title,
        children: standardsInNewSet.filter((item) => item.id !== standard.id),
        viewmodel_type,
      });
    }

    if (isEditMode && editData) {
      setConfirmModalData({
        setId: editData.id,
        editData: {
          ...editData,
          children: editData.children.filter((item) => item.id !== standard.id),
        },
      });
    }
  };

  const shouldDisplayAddToSet = useMemo(
    () =>
      standardsSetMode &&
      ((isEditMode && !isStandardOnEditSet) ||
        (isCreateMode && !isStandardInNewSet)),
    [standardsSetMode, isStandardInNewSet, isStandardOnEditSet],
  );

  return (
    <div>
      {((isEditMode && isStandardOnEditSet) ||
        (isCreateMode && isStandardInNewSet)) && (
        <ButtonWithTrackEvent
          __cssFor={addRemoveButtonCss}
          onClick={removeStandardFromSet}
          objectType={objectTypes.content}
          actionName={actionNames.update}
          streamProperties={{
            description: "Remove standard from set that it is being edited",
          }}
          disabled={isUpdatingStandardsSet}
          dataProps={{
            "data-qa-selector": "remove-standard-from-set-button",
          }}
        >
          <Icon SvgComponent={MinusSVG} size={Icon.sizes.sm} />
          Remove from Set
        </ButtonWithTrackEvent>
      )}
      {shouldDisplayAddToSet && (
        <ButtonWithTrackEvent
          __cssFor={addRemoveButtonCss}
          onClick={addStandardToSet}
          objectType={objectTypes.content}
          actionName={actionNames.add}
          streamProperties={{
            description: "Add standard to set that it is being created/edited",
          }}
          disabled={isPublishingStandardsSet || isUpdatingStandardsSet}
          dataProps={{
            "data-qa-selector": "add-standard-to-set-button",
          }}
        >
          <Icon SvgComponent={PlusSVG} size={Icon.sizes.sm} />
          Add to Set
        </ButtonWithTrackEvent>
      )}
    </div>
  );
};
